<template>
  <div
    class="carousel"
    :class="{ 'carousel--pager': hasPager }"
    :style="`--dotcom-carousel-transition-duration: ${transitionDuration}ms;`"
    tabindex="0"
    @touchstart="onPointerDown"
    @touchmove="onPointerMove"
    @touchend="onPointerUp"
    @mousedown="onPointerDown"
    @mousemove="onPointerMove"
    @mouseup="onPointerUp"
    @keydown="onKeyDown"
  >
    <div v-if="hasPager" class="carousel__pager">
      <button
        class="carousel__pager__previous"
        type="button"
        :disabled="!hasPrevious"
        data-track-text="appScreens.slider.previous"
        @click="onClickPrevious"
      >
        <DotcomIcon
          name="chevron-right"
          art="regular"
          class="carousel__pager__previous__icon"
          rotation="180"
          :color="
            hasPrevious ? '--c-primary-neutral-1' : '--c-secondary-neutral-2'
          "
          hover-color="--c-primary-color-2"
        />
      </button>

      <button
        class="carousel__pager__next"
        type="button"
        :disabled="!hasNext"
        data-track-text="appScreens.slider.next"
        @click="onClickNext"
      >
        <DotcomIcon
          name="chevron-right"
          art="regular"
          class="carousel__pager__next__icon"
          :rotation="null"
          :color="hasNext ? '--c-primary-neutral-1' : '--c-secondary-neutral-2'"
          hover-color="--c-primary-color-2"
        />
      </button>
    </div>

    <slot :index="index" />

    <ul v-if="hasPagination" class="carousel__pagination">
      <li
        v-for="page in pages"
        :key="page.number"
        class="carousel__pagination__page"
        :class="{ 'carousel__pagination__page--active': index === page.index }"
      >
        <button
          class="carousel__pagination__button"
          type="button"
          data-track-text="appScreens.slider.dot"
          @click="onClickPage(page.index)"
        >
          Page {{ page.number }}
        </button>
      </li>
    </ul>
  </div>
</template>

<script>
import { trackAction, tracker } from '@/helpers/tracker'

import CarouselQueue from './CarouselQueue'

/** Normalizes mouse and touch events. */
function normalizeEvent(event) {
  return event.changedTouches ? event.changedTouches[0] : event
}

export default {
  name: 'SanitasCarousel',

  components: {
    DotcomIcon: () => {
      return import(
        /* webpackChunkName: "dotcom-icon" */
        '@/modules/icon/Icon'
      )
    },
  },

  props: {
    initialIndex: {
      type: Number,
      default: 0,
    },
    canLoop: {
      type: Boolean,
      default: false,
    },

    pageCount: {
      type: Number,
      required: true,
    },
    showPager: {
      type: Boolean,
      default: false,
    },
    showPagination: {
      type: Boolean,
      default: false,
    },

    autoplay: {
      type: Boolean,
      default: false,
    },
    autoplayDelay: {
      type: Number,
      default: 4000,
    },
    transitionDuration: {
      type: Number,
      default: 800,
    },
  },

  data() {
    return {
      index: parseInt(this.initialIndex),
      isPlaying: false,
    }
  },

  computed: {
    hasPager() {
      return this.showPager && this.pageCount > 1
    },
    hasPrevious() {
      return this.canLoop || this.index > 0
    },
    hasNext() {
      return this.canLoop || this.index <= this.pageCount - 2
    },
    hasPagination() {
      return this.showPagination && this.pageCount > 1
    },
    pages() {
      let pages = []
      for (let i = 0; i < this.pageCount; i++) {
        pages.push({
          number: i + 1,
          index: i,
        })
      }
      return pages
    },
  },

  mounted() {
    this.queue = window.queue = new CarouselQueue(this.transitionDuration)

    if (this.autoplay) {
      this.startPlaying()
    }
  },
  beforeDestroy() {
    this.stopPlaying()
    this.queue.destroy()
  },

  methods: {
    goToPrevious() {
      // Add pageCount to allow index-1 to be negative.
      this.index = (this.index - 1 + this.pageCount) % this.pageCount
    },
    goToNext() {
      this.index = (this.index + 1) % this.pageCount
    },

    // Click interactions

    onClickPrevious() {
      this.stopPlaying()
      this.queue.add(this.goToPrevious)
    },
    onClickNext() {
      this.stopPlaying()
      this.queue.add(this.goToNext)
    },
    onClickPage(page) {
      this.stopPlaying()
      this.queue.clear()
      this.queue.add(() => (this.index = page))
    },

    // Keyboard interactions

    onKeyDown(event) {
      switch (event.key) {
        case 'ArrowLeft':
          this.queue.add(this.goToPrevious)
          break
        case 'ArrowRight':
          this.queue.add(this.goToNext)
          break
      }
    },

    // Swipe interactions

    onPointerDown(event) {
      this.pointerStartX = normalizeEvent(event).screenX
    },
    onPointerMove(event) {
      // DO NOT PREVENT DEFAULT!
      // That will also disable vertical scrolling.

      if (this.pointerStartX) {
        this.pointerStopX = normalizeEvent(event).screenX
      }
    },
    onPointerUp(event) {
      if (this.pointerStartX && this.pointerStopX) {
        let buffer = 50
        let distance = this.pointerStartX - this.pointerStopX

        if (distance + buffer < 0) {
          this.trackSwipe('swipeLeft')
          this.onClickPrevious()
        } else if (distance - buffer > 0) {
          this.trackSwipe('swipeRight')
          this.onClickNext()
        }

        this.pointerStartX = null
        this.pointerStopX = null
      }
    },

    // Auto-play

    startPlaying() {
      if (this.isPlaying) {
        return
      }

      this.isPlaying = true
      this.autoplayInterval = setInterval(
        () => this.queue.add(this.goToNext),
        parseInt(this.autoplayDelay),
      )
    },
    stopPlaying() {
      clearInterval(this.autoplayInterval)
      this.isPlaying = false
    },

    // Tracking

    trackSwipe(direction) {
      tracker.set({ data: { id: 'appScreens' } })
      trackAction(`appScreens.slider.${direction}`)
    },
  },
}
</script>

<style lang="postcss" scoped>
.carousel {
  position: relative;
  outline: none;

  &:focus {
    /* outline: 2px dotted var(--c-primary-neutral-1); */
  }

  &__pager {
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: var(--dotcom-carousel-display-height);
    font-size: 20px;

    &__previous,
    &__next {
      display: inline-block;
      outline: none;
      border: none;
      padding: 0;
      background-color: transparent;
      cursor: pointer;

      &:focus {
        /* outline: 2px dotted var(--c-primary-neutral-1); */
      }
    }
  }

  &__pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: var(--spacing-6) 0;
    gap: var(--spacing-2);
    list-style: none;

    &__button {
      padding: var(--spacing-2);
      display: block;
      width: var(--dotcom-carousel-pagination-size);
      height: var(--dotcom-carousel-pagination-size);
      padding: 0;
      list-style: none;
      border: 1px solid #d1d1d1;
      border-radius: var(--dotcom-carousel-pagination-size);
      background-color: #d1d1d1;
      overflow: hidden;
      transition: background-color 0.8s, border-color 0.8s;
      cursor: pointer;
      text-indent: 100%;
      overflow: hidden;
      white-space: nowrap;
      outline: none;

      .carousel__pagination__page--active & {
        background-color: var(--c-primary-neutral-1);
        border-color: var(--c-primary-neutral-1);
      }

      &:focus {
        /* outline: 2px dotted var(--c-primary-neutral-1); */
      }
    }
  }
}

@media (--md) {
  .carousel__pagination {
    padding: var(--spacing-8) 0;
  }
}
</style>
